/**
 * ChallengeNode — Diamond-Shaped Challenge on the Cosmic Map
 * ───────────────────────────────────────────────────────────
 * Extracted from CosmicMap. Renders a diamond node with:
 *   - Open challenge pulse (no responses yet)
 *   - Selection/highlight glow
 *   - Proximity bloom scaling
 *   - Response count or "?" indicator
 *   - Title label below
 *
 * v3 Changes — Zoom-Reveal Disambiguation:
 *   - Accepts a `zoom` prop (default 1) and uses hitRadiusForZoom() to
 *     keep the invisible hit-circle at a constant *screen pixel* size
 *     regardless of current zoom. See mapConstants.jsx docs.
 *   - Behavior at zoom 1.0 is identical to before.
 *   - Backwards-compatible: missing `zoom` prop falls back to zoom=1.
 *
 * Place at: src/components/map/ChallengeNode.jsx
 */

import React from 'react';
import {
  CHALLENGE_SIZE, CHALLENGE_SIZE_MAX, CHALLENGE_HIT_AREA,
  BLOOM_EASE, BLOOM_DURATION, lerp, hitRadiusForZoom,
} from './mapConstants';

const ChallengeNode = ({
  node, isSelected, isHighlighted, isDimmed, bloomFactor, hitRadius, zoom = 1,
  onClick, onDoubleClick, onMouseEnter, onMouseLeave,
  onPointerDown, onPointerUp,
}) => {
  const challenge = node.challenge;
  const bf = bloomFactor || 0;
  const bloomScale = lerp(1, CHALLENGE_SIZE_MAX / CHALLENGE_SIZE, bf);
  const size = CHALLENGE_SIZE;

  // Zoom-aware hit radius — see ProjectNode for rationale.
  const baseHitArea = hitRadius || CHALLENGE_HIT_AREA;
  const effectiveHitR = hitRadiusForZoom(baseHitArea, zoom);

  const diamondPath = `M 0 ${-size/2} L ${size/2} 0 L 0 ${size/2} L ${-size/2} 0 Z`;
  const responseCount = challenge.responseCount || challenge.response_count || 0;
  const isOpen = responseCount === 0;

  return (
    <g
      transform={`translate(${node.x}, ${node.y})`}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); onClick?.(node.challenge); }}
      onDoubleClick={(e) => { e.stopPropagation(); onDoubleClick?.(node.challenge); }}
      onPointerDown={(e) => { onPointerDown?.(); }}
      onPointerUp={(e) => { onPointerUp?.(); }}
      onPointerCancel={(e) => { onPointerUp?.(); }}
      onMouseEnter={(e) => onMouseEnter?.(node, e.nativeEvent)}
      onMouseLeave={() => onMouseLeave?.()}
      style={{ cursor: 'pointer', opacity: isDimmed ? 0.08 : 1, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
    >
      {/* Bloom wrapper */}
      <g style={{
        transform: `scale(${bloomScale})`,
        transition: `transform ${BLOOM_DURATION} ${BLOOM_EASE}`,
        transformOrigin: '0 0',
      }}>
        {/* Invisible hit area — zoom-aware */}
        <circle r={effectiveHitR} fill="transparent" />

        {isOpen && !isDimmed && (
          <path d={diamondPath} fill="none" stroke="#f59e0b" strokeWidth={1} opacity={0} transform="scale(1.6)">
            <animate attributeName="opacity" values="0;0.3;0" dur="2.5s" repeatCount="indefinite" />
            <animateTransform attributeName="transform" type="scale" values="1.3;1.7;1.3" dur="2.5s" repeatCount="indefinite" />
          </path>
        )}

        {(isSelected || isHighlighted) && (
          <path d={diamondPath} fill="none" stroke="#f59e0b" strokeWidth={3} opacity={0.4} transform="scale(1.5)">
            <animate attributeName="opacity" values="0.3;0.6;0.3" dur="2s" repeatCount="indefinite" />
          </path>
        )}

        {/* Bloom glow */}
        {bf > 0.05 && !isDimmed && (
          <path d={diamondPath} fill="#f59e0b" opacity={bf * 0.12} transform="scale(1.3)" />
        )}

        <path d={diamondPath} fill="#1c1917" stroke="#f59e0b" strokeWidth={isSelected || isHighlighted ? 2.5 : 1.8} />

        {isOpen ? (
          <text textAnchor="middle" dominantBaseline="central" fill="#fbbf24" fontSize="16" fontWeight="700">?</text>
        ) : (
          <text textAnchor="middle" dominantBaseline="central" fill="#f59e0b" fontSize="13" fontWeight="bold">
            {responseCount}
          </text>
        )}
      </g>

      <text y={size / 2 * bloomScale + 14} textAnchor="middle" fill="#92400e" fontSize="9" fontWeight="500" style={{ pointerEvents: 'none' }}>
        {(challenge.title || challenge.challenge_title || 'Challenge')?.slice(0, 16)}
        {(challenge.title || challenge.challenge_title || 'Challenge')?.length > 16 ? '\u2026' : ''}
      </text>
    </g>
  );
};

export default React.memo(ChallengeNode);
